import { useCallback, useMemo, useState } from "react";

/**
 * Filtros de uma lista no modelo do ft-app-web (useFilters + store de filtros),
 * com o estado na própria tela no lugar do Zustand.
 *
 * - `storeFilters`: os filtros aplicados ({ filters, setFilters, removeFilters }),
 *   no formato que o `TableFilters` da lib espera. É dele que a tela monta a query.
 * - `filter`: o rascunho (busca digitada e campos do `DrawerFilterPanel`), que só
 *   vira filtro aplicado no "Confirmar" (a busca, depois do debounce do `TableFilters`).
 * - `hasFilter`: quantos filtros aplicados há, sem contar o status (as pílulas).
 *
 * `initialFilters` costuma trazer o status inicial (a pílula "Todos").
 */
export default function useListFilters(initialFilters, setPagination) {
  const [filters, setFiltersState] = useState(initialFilters);
  const [filter, setFilter] = useState({});
  const [openFilter, setOpenFilter] = useState(false);

  const resetPage = useCallback(() => setPagination((prev) => ({ ...prev, pageIndex: 0 })), [setPagination]);

  const storeFilters = useMemo(
    () => ({
      filters,
      setFilters: (data) => {
        // Só troca o estado quando algum valor muda (o TableFilters chama a cada render do debounce)
        setFiltersState((prev) => (Object.keys(data).some((key) => prev[key] !== data[key]) ? { ...prev, ...data } : prev));
        if ("status" in data) resetPage();
      },
      removeFilters: () => setFiltersState((prev) => ({ ...initialFilters, status: prev.status })),
    }),
    // initialFilters é constante da tela
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, resetPage]
  );

  const handleSetFilters = (event, name, value) => {
    if (event) {
      name = event.target.name;
      value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    }
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmFilter = () => {
    storeFilters.setFilters(filter);
    resetPage();
    setOpenFilter(false);
  };

  const handleClearFilters = () => {
    storeFilters.removeFilters();
    setFilter({});
    resetPage();
    setOpenFilter(false);
  };

  const hasFilter = Object.entries(filters).filter(([key, value]) => key !== "status" && Boolean(value)).length;

  return {
    storeFilters,
    filters,
    filter,
    openFilter,
    setOpenFilter,
    handleSetFilters,
    handleConfirmFilter,
    handleClearFilters,
    hasFilter,
  };
}
